import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { PageTitlesRetrievalSelectionService } from '../page-titles-retrieval-selection.service';
import { PipelineStatus } from '../../../_study_constants';
import { PageTitleRetrieval } from '../../../../models/pageTitleRetrieval.model';
import * as _ from 'lodash-es';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { selectScraperProgressComparablesStatusByStudy, selectScraperDebuggingInfoByStudy } from '../scraper-progress/scraper-progress.selectors';
import { select, Store } from '@ngrx/store';
import { ScraperItemProgress } from './scraper-item-progress.model';
import { ScraperProgressService } from '../scraper-progress/scraper-progress.service';
import { saveAs } from 'file-saver-es';
import { ScraperProgressDialogService } from './scraper-progress-dialog.service';

@Component({
  selector: 'app-scraper-progress-dialog',
  templateUrl: './scraper-progress-dialog.component.html',
  styleUrls: ['./scraper-progress-dialog.component.css'],
})
export class ScraperProgressDialogComponent implements OnInit, OnDestroy {
  public studyId: number;
  public studySearchStrategyId: number;
  public timer: number;
  public timerTotalDuration: string;
  public scrapeJobId: string;
  public timerLastProgressUpdate: number;
  public timerCreatedAt: number;
  public isDebugInfo: boolean;
  private timerIntervalId: any;
  private destroy$ = new Subject();
  items: PageTitleRetrieval[];
  showLoader: boolean;
  statuses: Record<number, ScraperItemProgress>;
  progressUpdatedSubscription: any;

  constructor(
    private activeModal: NgbActiveModal,
    private selectionService: PageTitlesRetrievalSelectionService,
    private store: Store<any>,
    private scraperProgressService: ScraperProgressService,
    private scraperProgressDialogService: ScraperProgressDialogService
  ) {}

  ngOnInit() {
    this.showLoader = true;

    this.items = _.filter(_.sortBy(this.selectionService.getItemsByStudyId(this.studyId), 'name'), 'name');
    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectScraperProgressComparablesStatusByStudy(this.studyId)))
      .subscribe((comparableIds) => {
        this.getStatuses(comparableIds);
        if (this.items.length == 0) {
          this.loadCompanyNames(comparableIds);
        }
      });

    this.timerIntervalId = setInterval(() => {
      this.timerUpdate();
    }, 1000);
    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectScraperDebuggingInfoByStudy(this.studyId)))
      .subscribe((debugInfo) => {
        if (!!debugInfo && (debugInfo.lastUpdate || debugInfo.scrapeJobId)) {
          this.isDebugInfo = true;
          this.timerLastProgressUpdate = debugInfo.lastUpdate;
          this.timerCreatedAt = debugInfo.createdAt;
          this.scrapeJobId = debugInfo.scrapeJobId;
        }
      });
    this.showLoader = false;
  }

  loadCompanyNames(comparableIds: Record<number, ScraperItemProgress>) {
    this.showLoader = true;
    const companyIds = Object.keys(comparableIds).map(Number);
    this.scraperProgressDialogService
      .getCompanyNames(this.studyId, companyIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.items = _.map(companyIds, function (companyId) {
          return new PageTitleRetrieval(companyId, result[companyId]);
        });
        this.showLoader = false;
      });
  }

  getStatuses(comparableIds: Record<number, ScraperItemProgress>) {
    this.statuses = comparableIds;
    this.showLoader = false;
  }

  downloadScreenshot(item: PageTitleRetrieval) {
    const itemProgress = this.statuses[item.id];
    this.scraperProgressService.downloadScreenshot(this.studyId, itemProgress.id).subscribe((response) => {
      saveAs(response, 'Scrape results for (' + item.id + ') study ' + this.studyId);
    }),
      () => console.log('Error downloading the file'),
      () => console.info('File downloaded successfully');
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    clearInterval(this.timerIntervalId);
  }

  getStatusDescription(item: PageTitleRetrieval) {
    if (this.statuses) {
      const status = this.statuses[item.id];
      if (status) {
        switch (status.status) {
          case PipelineStatus.Pending:
            return 'Not started';
          case PipelineStatus.Active:
            return 'In progress';
          case PipelineStatus.Ended:
            return 'Completed';
          case PipelineStatus.EndedWithError:
            return 'Ended with an error';
        }
      }
    }
    return 'Not started';
  }

  isStatusCompleted(item: PageTitleRetrieval) {
    if (!!this.statuses && !!this.statuses[item.id]) return this.statuses[item.id].status == PipelineStatus.Ended;
    else return false;
  }

  public close() {
    this.activeModal.close();
  }

  private timerUpdate() {
    const now = Math.floor(Date.now() / 1000);
    if (this.timerLastProgressUpdate > 0) this.timer = now - this.timerLastProgressUpdate;

    if (this.timerCreatedAt > 0) {
      const totalDuration = now - this.timerCreatedAt;
      const days = Math.floor(totalDuration / (24 * 3600));
      const daysLeft = totalDuration % (24 * 3600);

      const hours = Math.floor(daysLeft / 3600);
      const minutesLeft = totalDuration % 3600;
      const minutes = Math.floor(minutesLeft / 60);
      const seconds = minutesLeft % 60;

      this.timerTotalDuration = days + 'd ' + hours + 'h ' + minutes + 'm ' + seconds + 's';
    }
  }
}
